import { useState } from 'react'
import { ExternalLink } from 'lucide-react'

import type { SubscriptionStatus } from '~/features/billing/types'

import { Button } from '~/elements/Button'
import { Skeleton } from '~/elements/Skeleton'
import {
  useBillingPortal,
  useCurrentWorkspacePlan,
  useSubscriptionMutation
} from '~/features/billing/hooks/useBillingHooks'
import { formatDate, formatUsd } from '~/features/billing/utils'
import { ConfirmBillingDialog } from '~/components/billing/ConfirmBillingDialog'
import { cn } from '~/lib/utils'

const STATUS_LABELS: Record<SubscriptionStatus, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Payment failed',
  paused: 'Paused',
  canceled: 'Canceled',
  free: 'Free'
}

export function SubscriptionSummary({ className }: { className?: string }) {
  const { state, name, loading } = useCurrentWorkspacePlan()
  const portal = useBillingPortal()
  const subscription = useSubscriptionMutation()
  const [confirmCancel, setConfirmCancel] = useState(false)

  if (loading) {
    return <Skeleton className={cn('h-32 w-full rounded-xl', className)} enabled />
  }

  if (!state) return null

  const hasSubscription = Boolean(state.paddle?.subscriptionId) && state.plan !== 'free'
  const cancelAt = state.scheduledChange?.action === 'cancel' ? state.scheduledChange.effectiveAt : null
  const pauseAt = state.scheduledChange?.action === 'pause' ? state.scheduledChange.effectiveAt : null
  const pastDue = state.status === 'past_due'

  return (
    <div className={cn('flex flex-col gap-4 rounded-xl border bg-fill2 p-5', className)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium tracking-wide text-content3 uppercase">Current plan</span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xl font-bold text-content">{name}</span>
            {state.interval && (
              <span className="text-sm text-content2">
                billed {state.interval === 'year' ? 'yearly' : 'monthly'}
              </span>
            )}
            <span
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                pastDue || state.status === 'canceled' ? 'border-danger/40 text-danger' : 'text-content2'
              )}
            >
              {STATUS_LABELS[state.status] ?? state.status}
            </span>
          </div>
          <p className="text-sm text-content2">
            {cancelAt ?
              `Cancels on ${formatDate(cancelAt)}, then moves to Free.`
            : pauseAt ?
              `Pauses on ${formatDate(pauseAt)}.`
            : state.period ?
              `Usage window ${formatDate(state.period.start)} – ${formatDate(state.period.end)} · estimated ${formatUsd(state.estimate.total)}`
            : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {pastDue && (
            <Button
              loading={portal.isPending}
              onClick={() => portal.open('updatePaymentMethod')}
              size="small"
              variant="danger"
            >
              Update payment method
            </Button>
          )}
          {cancelAt && (
            <Button
              loading={subscription.isPending}
              onClick={() => subscription.mutate({ action: 'resume' })}
              size="small"
              variant="primary"
            >
              Resume subscription
            </Button>
          )}
          {state.paddle?.customerId && (
            <Button
              loading={portal.isPending}
              onClick={() => portal.open('overview')}
              size="small"
              variant="secondary"
            >
              Manage billing <ExternalLink />
            </Button>
          )}
          {hasSubscription && !cancelAt && (
            <Button onClick={() => setConfirmCancel(true)} size="small" variant="dangerGhost">
              Cancel subscription
            </Button>
          )}
        </div>
      </div>

      <ConfirmBillingDialog
        confirmLabel="Cancel subscription"
        danger
        description={
          <>
            <p>
              Your subscription stays active until the end of the current billing period. After that the
              workspace moves to the Free plan and its limits.
            </p>
            <p>You can resume the subscription any time before it ends.</p>
          </>
        }
        loading={subscription.isPending}
        onConfirm={() => subscription.mutateAsync({ action: 'cancel' })}
        onOpenChange={setConfirmCancel}
        open={confirmCancel}
        title="Cancel your subscription?"
      />
    </div>
  )
}
